import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import styles from "../Caches.module.css";
import LruEngine from "../mapengine/LruEngine";
import LfuEngine, { LFUNode } from "../mapengine/LfuEngine";

const CompareCache: React.FC = () => {
  const navigate = useNavigate();
  const [capacity, setCapacity] = useState(4);
  const [keyInput, setKeyInput] = useState("");
  const [valInput, setValInput] = useState("");
  const [getKey, setGetKey] = useState("");
  const [lastResult, setLastResult] = useState<string | null>(null);
  const lru = LruEngine(capacity);
  const lfu = LfuEngine(capacity);

  const handleCapacityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = Number(e.target.value);
    const newCap = isNaN(raw) || raw < 1 ? 1 : raw;
    setCapacity(newCap);
    lru.reset();
    lfu.reset();
    setLastResult(null);
  };

  const handleReset = () => {
    lru.reset();
    lfu.reset();
    setLastResult(null);
    setKeyInput("");
    setValInput("");
    setGetKey("");
  };

  const handlePut = () => {
    if (!keyInput || !valInput) return;
    lru.put(keyInput, valInput);
    lfu.put(keyInput, valInput);
    setLastResult(null);
  };

  const handleGet = () => {
    if (!getKey) return;
    const lruHit = lru.get(getKey);
    const lfuHit = lfu.get(getKey);
    if (lruHit === null && lfuHit === null) {
      setLastResult(`Miss: ${getKey}`);
    } else {
      setLastResult(`Hit: ${lruHit ?? lfuHit}`);
    }
  };

  const handleRandom = () => {
    lru.reset();
    lfu.reset();
    setLastResult(null);

    const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

    const count = Math.min(capacity, alphabet.length);
    for (let i = 0; i < count; i++) {
      const key = alphabet[i];
      const value = String(Math.floor(Math.random() * 90) + 10);
      lru.put(key, value);
      lfu.put(key, value);
    }
  };

  const sharedMap = Array.from(lru.map.values());

  const freqGroups = useMemo(
    () => groupByFrequency(Array.from(lfu.map.values())),
    [lfu.map]
  );

  return (
    <div className={styles.cachePageContainer}>
      <button onClick={() => navigate(-1)} className={styles.returnButton}>
        ← Return
      </button>

      <h1 className={styles.title}>Compare Caches (LRU vs LFU)</h1>

      <p className={styles.description}>
        Imagine two assistants managing the same workspace but following different rituals. <strong>An LRU</strong> cache 
        keeps what you touched most recently like placing your latest tools on the top of the pile. <strong>An LFU</strong> cache 
        keeps what you use most often like shelving items by popularity instead of recency.
        Both receive the same key–value pairs, but each decides a completely different thing to discard when space runs out.
        Same data, same capacity, different survival rules and those rules dictate how fast your system feels under real-world usage.
      </p>

      <div className={styles.controlsBar}>
        <input
          type="number"
          value={capacity}
          min={1}
          onChange={handleCapacityChange}
          className={`${styles.controlInput} ${styles.capacityInput}`}
        />
        <button className={styles.cacheButton}>Capacity</button>

        <input
          placeholder="key"
          value={keyInput}
          onChange={(e) => setKeyInput(e.target.value)}
          className={styles.controlInput}
        />
        <input
          placeholder="value"
          value={valInput}
          onChange={(e) => setValInput(e.target.value)}
          className={styles.controlInput}
        />
        <button className={styles.cacheButton} onClick={handlePut}>
          Put
        </button>

        <input
          placeholder="key"
          value={getKey}
          onChange={(e) => setGetKey(e.target.value)}
          className={styles.controlInput}
        />
        <button className={styles.cacheButton} onClick={handleGet}>
          Get
        </button>

        <button className={styles.cacheButton} onClick={handleReset}>
          Reset
        </button>

        <button
          className={`${styles.cacheButton} ${styles.randomButton}`}
          onClick={handleRandom}
        >
          Random
        </button>
      </div>

      {lastResult && (
        <p style={{ textAlign: "center", marginTop: "0.4rem" }}>
          {lastResult}
        </p>
      )}

      <div className={styles.sectionCard}>
        <h3>Hash Map (key → node)</h3>

        <div className={styles.arrayDisplay}>
          <span className={styles.arrayBracket}>[</span>
          <div className={styles.arrayItems}>
            {sharedMap.map((node, i) => (
              <div key={node.key} className={styles.arrayItem}>
                {`{ ${node.key}: ${node.value} }`}
                {i < sharedMap.length - 1 && (
                  <span className={styles.arrayComma}>,</span>
                )}
              </div>
            ))}
          </div>
          <span className={styles.arrayBracket}>]</span>
        </div>
      </div>

      <div className={styles.compareGrid}>
        <div className={styles.compareColumn}>
          <h2 className={styles.compareTitle}>LRU Cache</h2>

          <div className={styles.sectionCard}>
            <h3>Recently Bucket (LRU)</h3>
            
            <div className={styles.dllContainer}>
              <div className={styles.dllEnd}>HEAD</div>
              <div className={styles.dllArrow}>⇄</div>

              {lru.list.length === 0 && (
                <div className={styles.emptyMsg}>List empty.</div>
              )}

              {lru.list.map((node, idx) => (
                <React.Fragment key={node.id}>
                  <div className={styles.dllNode}>
                    <div className={styles.dllKey}>{node.key}</div>
                    <div className={styles.dllVal}>{node.value}</div>
                  </div>
                  {idx < lru.list.length - 1 && (
                    <div className={styles.dllArrow}>⇄</div>
                  )}
                </React.Fragment>
              ))}

              <div className={styles.dllArrow}>⇄</div>
              <div className={styles.dllEnd}>TAIL</div>
            </div>
          </div>
        </div>

        <div className={styles.compareColumn}>
          <h2 className={styles.compareTitle}>LFU Cache</h2>

          <div className={styles.sectionCard}>
            <h3>Frequency Buckets (LFU)</h3>

            <div className={styles.freqGroupsColumn}>
              {freqGroups.map((group) => (
                <div key={group.freq} className={styles.freqGroupCard}>
                  <div className={styles.freqHeader}>freq = {group.freq}</div>

                  <div className={styles.dllContainer}>
                    <div className={styles.dllEnd}>HEAD</div>
                    <div className={styles.dllArrow}>⇄</div>

                    {group.nodes.length === 0 && (
                      <div className={styles.emptyMsg}>Empty</div>
                    )}

                    {group.nodes.map((node, idx) => (
                      <React.Fragment key={node.id}>
                        <div className={styles.dllNode}>
                          <div className={styles.dllKey}>{node.key}</div>
                          <div className={styles.dllVal}>{node.value}</div>
                        </div>
                        {idx < group.nodes.length - 1 && (
                          <div className={styles.dllArrow}>⇄</div>
                        )}
                      </React.Fragment>
                    ))}

                    <div className={styles.dllArrow}>⇄</div>
                    <div className={styles.dllEnd}>TAIL</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

function groupByFrequency(nodes: LFUNode[]) {
  const m = new Map<number, LFUNode[]>();

  for (const n of nodes) {
    if (!m.has(n.freq)) m.set(n.freq, []);
    m.get(n.freq)!.push(n);
  }

  return Array.from(m.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([freq, nodes]) => ({ freq, nodes }));
}

export default CompareCache;
